"use client";
import React from "react";
import { IBlocks, IUserData } from "@/types";
import { Label } from "@/components/ui/label";
import Link from "next/link";
import moment from "moment";
import type { BlockUpdateFunction } from "./utils/blocks-map";
import AdminPopover from "./utils/AdminPopover";
import LoadingSkeleton from "@/components/admin/profile/blocks/LoadingSkeleton";
import { cn } from "@/lib/utils";
import {
    BORDER_RADIUS_OPTIONS,
    SHADOW_OPTIONS,
} from "./utils/block-style-classes";

interface Props {
    edit?: boolean;
    account: IUserData;
    block: IBlocks;
    update?: BlockUpdateFunction;
    style: any;
    theme: IUserData["chosen_theme"];
}

export default function BlockPodbean({
    account,
    edit,
    block,
    update,
    style,
    theme,
}: Props) {
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [rssData, setRssData] = React.useState<any>(null);
    React.useEffect(() => {
        const fetchRssData = async () => {
            try {
                const response = await fetch(blockUpdate.url);
                const data = await response.text();
                const parser = new DOMParser();
                const xml = parser.parseFromString(data, "application/xml");
                const title = xml.querySelector("title")?.textContent || "";
                const description =
                    xml.querySelector("description")?.textContent || "";
                const urlElement = xml.querySelector("image > url");
                const image = urlElement
                    ? urlElement?.textContent?.trim()
                    : null;
                const items = xml.querySelectorAll("item");
                const item = {
                    title: items[0].querySelector("title")?.textContent || "",
                    link: items[0].querySelector("link")?.textContent || "",
                    description:
                        items[0].querySelector("description")?.textContent ||
                        "",
                    pubDate:
                        items[0].querySelector("pubDate")?.textContent || "",
                };
                setRssData({
                    title,
                    description,
                    image,
                    item,
                });
            } catch (error) {
                setRssData(null);
                console.error("Error fetching RSS data:", error);
            }
        };
        // Fetch RSS feed data when the component mounts
        fetchRssData();
    }, []);
    return (
        <AdminPopover
            edit={edit}
            className={block.show ? "" : "opacity-30"}
            title={`PodBean | ${rssData?.title || ""}`}
            block={block}
            update={update}
            setBlockUpdate={setBlockUpdate}
        >
            <div className="w-full">
                {rssData ? (
                    <div
                        className={`space-y-3
                    ${account.theme?.button_shadow}
                    ${account.theme?.button_rounded == "rounded-full" ? "rounded-2xl" : account.theme?.button_rounded} block-${block.id}
                    `}
                        style={style}
                    >
                        <Link
                            className={`w-full`}
                            href={rssData.item.link}
                            target="_blank"
                        >
                            <button
                                style={style}
                                id="podbean"
                                className={cn(
                                    "relative rounded-user bg-user-primary-color text-user-primary-text-color",
                                    SHADOW_OPTIONS[theme.profile.design.shadow]
                                        ?.class,
                                    blockUpdate.style?.custom &&
                                        BORDER_RADIUS_OPTIONS[
                                            blockUpdate.style.corner_radius
                                        ]?.class,
                                    blockUpdate.style?.custom &&
                                        SHADOW_OPTIONS[blockUpdate.style.shadow]
                                            ?.class,
                                    "w-full px-1 py-3 text-xl shadow-md transition-all duration-150 hover:scale-105",
                                    blockUpdate.name.replaceAll(" ", "-")
                                )}
                            >
                                <div className={`flex justify-start gap-1`}>
                                    <div className="w-1/3">
                                        <img
                                            src={rssData.image}
                                            className={`h-auto ${account.theme?.button_rounded == "rounded-full" ? "rounded-2xl" : account.theme?.button_rounded}`}
                                            alt="custom icons"
                                        />
                                    </div>
                                    <div className="flex w-2/3 flex-col gap-2 px-2">
                                        <span
                                            id="block-text"
                                            className={`w-full text-start font-bold`}
                                        >
                                            {rssData.title}
                                        </span>
                                        <Label
                                            className="line-clamp-3 text-start"
                                            style={{ color: style.color }}
                                        >
                                            {rssData.description}
                                        </Label>
                                        <div className="mt-auto">
                                            <Label
                                                className="text-start font-bold"
                                                style={{ color: style.color }}
                                            >
                                                Latest Episode:{" "}
                                                {rssData.item.title}
                                            </Label>
                                            <Label
                                                className="text-start"
                                                style={{ color: style.color }}
                                            >
                                                Uploaded:{" "}
                                                {moment(
                                                    rssData.item.pubDate
                                                ).format("MMM Do YYYY")}
                                            </Label>
                                        </div>
                                    </div>
                                </div>
                            </button>
                        </Link>
                    </div>
                ) : (
                    <LoadingSkeleton className="bg-user-primary-color" />
                )}
            </div>
        </AdminPopover>
    );
}
